import { useState, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Upload,
  FileText,
  X,
  Loader2,
  CheckCircle2,
  Image as ImageIcon,
  File,
  AlertTriangle,
  ScanLine,
  Info,
  ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 0, label: "Upload Complete", icon: Upload },
  { id: 1, label: "Reading Invoice", icon: ScanLine },
  { id: 2, label: "Extracting Fields", icon: FileText },
  { id: 3, label: "Ready for Review", icon: CheckCircle2 },
];

export default function UploadPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressStep, setProgressStep] = useState(-1);
  const [sessionId] = useState(() => localStorage.getItem("sessionId") || `session_${Date.now()}`);

  useEffect(() => {
    localStorage.setItem("sessionId", sessionId);
    if (searchParams.get("sample") === "true") {
      handleSample();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 10 MB.");
        return;
      }
      setFile(selectedFile);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    maxFiles: 1,
  });

  const advanceProgress = async () => {
    for (let i = 0; i < 3; i++) {
      setProgressStep(i);
      await new Promise((r) => setTimeout(r, 650));
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }
    setIsProcessing(true);
    setProgressStep(0);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("processing_mode", "save");
    formData.append("session_id", sessionId);

    try {
      const [response] = await Promise.all([
        axios.post(`${API}/invoice/analyze`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
        advanceProgress(),
      ]);

      setProgressStep(3);
      await new Promise((r) => setTimeout(r, 400));

      if (response.data.success) {
        toast.success("Fields extracted — please review");
        navigate("/results", { state: { result: response.data.data, reviewRequired: true } });
      } else {
        throw new Error(response.data.error || "Extraction failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Failed to read invoice");
      setIsProcessing(false);
      setProgressStep(-1);
    }
  };

  const handleSample = async () => {
    setIsProcessing(true);
    setProgressStep(0);
    try {
      const [response] = await Promise.all([
        axios.post(`${API}/invoice/analyze-sample`, null, {
          params: { processing_mode: "save", session_id: sessionId },
        }),
        advanceProgress(),
      ]);
      setProgressStep(3);
      await new Promise((r) => setTimeout(r, 400));
      if (response.data.success) {
        toast.success("Sample fields extracted — please review");
        navigate("/results", { state: { result: response.data.data, reviewRequired: true, isSample: true } });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load sample");
      setIsProcessing(false);
      setProgressStep(-1);
    }
  };

  const getFileIcon = () => {
    if (!file) return <Upload className="w-10 h-10 text-slate-400" />;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "pdf") return <FileText className="w-10 h-10 text-red-500" />;
    if (["png", "jpg", "jpeg"].includes(ext)) return <ImageIcon className="w-10 h-10 text-emerald-500" />;
    return <File className="w-10 h-10 text-slate-500" />;
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 sm:py-12" data-testid="upload-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Honest Hero */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-100 rounded-full mb-4">
            <ScanLine className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">OCR-assisted</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-3 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Upload &amp; Review Invoice
          </h1>
          <p className="text-slate-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Upload your supplier invoice and we&rsquo;ll try to extract relevant details using OCR.
            Please verify all fields before saving.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          {/* Upload column */}
          <div className="lg:col-span-2 space-y-4">
            {/* OCR notice */}
            <div
              className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 sm:p-5 flex items-start gap-3"
              data-testid="ocr-notice"
            >
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900 mb-1">OCR Extraction Notice</p>
                <p className="text-xs text-amber-800 leading-relaxed">
                  Extracted values may be inaccurate or misplaced. HSN codes may be read as amounts, GST percentages mismatched, item names incomplete, or dates misread. <span className="font-semibold">Please review carefully before adding as Purchase Invoice.</span>
                </p>
              </div>
            </div>

            {/* Dropzone */}
            <div
              {...getRootProps()}
              className={`rounded-2xl border-2 border-dashed p-8 sm:p-12 text-center cursor-pointer transition-all ${
                isDragActive
                  ? "border-blue-500 bg-blue-50"
                  : file
                    ? "border-emerald-300 bg-emerald-50/40"
                    : "border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50"
              }`}
              data-testid="dropzone"
            >
              <input {...getInputProps()} data-testid="file-input" />
              <motion.div animate={{ scale: isDragActive ? 1.02 : 1 }} className="flex flex-col items-center">
                {getFileIcon()}
                {file ? (
                  <div className="mt-4">
                    <p className="text-slate-900 font-semibold">{file.name}</p>
                    <p className="text-sm text-slate-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                      className="mt-3 text-sm text-red-500 hover:text-red-700 inline-flex items-center gap-1"
                      data-testid="remove-file-btn"
                    >
                      <X className="w-4 h-4" /> Remove file
                    </button>
                  </div>
                ) : (
                  <div className="mt-4">
                    <p className="text-slate-900 font-semibold text-base sm:text-lg">
                      {isDragActive ? "Drop your invoice here" : "Drag & drop your invoice"}
                    </p>
                    <p className="text-sm text-slate-500 mt-1.5">or click to browse</p>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Supported file types + max */}
            <div className="flex items-center justify-between px-1 text-xs text-slate-500">
              <div className="flex items-center gap-2 flex-wrap" data-testid="supported-types">
                <span className="font-medium text-slate-600">Supported:</span>
                <span className="px-2 py-0.5 bg-slate-100 rounded font-mono">PDF</span>
                <span className="px-2 py-0.5 bg-slate-100 rounded font-mono">JPG</span>
                <span className="px-2 py-0.5 bg-slate-100 rounded font-mono">PNG</span>
              </div>
              <span className="font-medium text-slate-600">Max 10 MB</span>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch gap-3 pt-2">
              <button
                onClick={handleUpload}
                disabled={!file || isProcessing}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="upload-fetch-btn"
              >
                {isProcessing ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                ) : (
                  <>Upload &amp; Fetch Details <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
              <button
                onClick={handleSample}
                disabled={isProcessing}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 disabled:opacity-50 transition-colors"
                data-testid="sample-btn"
              >
                <FileText className="w-4 h-4" />
                Try Sample Invoice
              </button>
            </div>
          </div>

          {/* How it works */}
          <div className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6" data-testid="how-it-works">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">How It Works</p>
            <ol className="space-y-4">
              {[
                "Upload your invoice (PDF or image).",
                "OCR reads the text from your invoice.",
                "We attempt to identify supplier, invoice no., date, amounts, GST & items.",
                "You review the extracted details.",
                "Save as Purchase Invoice.",
              ].map((text, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-sm text-slate-600 leading-relaxed">{text}</span>
                </li>
              ))}
            </ol>

            <div className="mt-5 pt-5 border-t border-slate-100">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-500 leading-relaxed">
                  Smart OCR-assisted invoice entry. No AI claims, no magic — just practical extraction followed by your review.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Post-upload progress stepper modal */}
        <AnimatePresence>
          {isProcessing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
              data-testid="processing-overlay"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl max-w-md w-full p-6 sm:p-8"
              >
                <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Reading your invoice…
                </h3>
                <p className="text-sm text-slate-500 mb-6">This usually takes under 10 seconds.</p>

                <ol className="space-y-3">
                  {STEPS.map((s, i) => {
                    const done = progressStep > i || progressStep === 3;
                    const active = progressStep === i;
                    const Icon = s.icon;
                    return (
                      <li key={s.id} className="flex items-center gap-3" data-testid={`progress-step-${s.id}`}>
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                            done
                              ? "bg-emerald-500 text-white"
                              : active
                                ? "bg-blue-600 text-white"
                                : "bg-slate-100 text-slate-400"
                          }`}
                        >
                          {done ? <CheckCircle2 className="w-4 h-4" /> : active ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                        </div>
                        <span className={`text-sm ${active ? "font-semibold text-slate-900" : done ? "text-slate-600" : "text-slate-400"}`}>
                          {s.label}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
